/**
 * 那一声"到点了"的**波形参数**与合成（只依赖 `AudioContext`，不依赖任何 DOM 状态）。
 *
 * 与 `estimateChimeAudio.ts` 分工：
 *  - 本文件只管"这一声长什么样"（音高、音量、包络）—— 因此可以拿一个**假的
 *    `AudioContext`** 直接测（见 `softChime.test.ts`：两个音、正弦、上行、
 *    音量很小、有渐入渐出、总时长不到 1 秒）；
 *  - `estimateChimeAudio.ts` 管"什么时候能放、被浏览器拒绝怎么办"。
 *
 * ## 为什么是"两个正弦音"而不是一段音频文件 / 一句语音
 *
 * 见 `estimateChimeAudio.ts` 的文件头。这里只说参数为什么是这几个数：
 *
 *  - **正弦**没有泛音，是"叮"不是"哔"（方波/锯齿那种才是警报器）；
 *  - **D5 (587.33Hz) → A5 (880Hz)** 是纯五度上行，短促、明亮、不刺耳；
 *    两个音都低于 1kHz（高频长音最容易让人烦）；
 *  - 每个音 **30ms 渐入 + 指数渐出**，避免爆音，也让结尾自然消失；
 *  - 峰值 **0.12**（12% 满量程）：比手机通知音轻，孩子不会吓一跳；
 *  - 整声 **约 0.6 秒**：听完就能继续做题，不占注意力。
 */

/** 一声里的一个音。 */
export interface SoftChimeNote {
  /** 音高（Hz）。 */
  frequency: number;
  /** 相对这一声起点的延迟（秒）。 */
  delaySeconds: number;
  /** 这个音持续多久（秒）。 */
  durationSeconds: number;
}

/**
 * 这一声的两个音，**从低到高**（上行听起来是"提醒"而不是"警告"）。
 *
 * ⚠️ 改这里就是改"提示音听起来什么样"：请保持正弦、低音量、短促、上行。
 */
export const SOFT_CHIME_NOTES: readonly SoftChimeNote[] = [
  // D5：先轻轻起个头
  { frequency: 587.33, delaySeconds: 0, durationSeconds: 0.22 },
  // A5：五度上行，尾巴稍长一点，自然收住
  { frequency: 880, delaySeconds: 0.18, durationSeconds: 0.42 },
];

/** 峰值音量（满量程比例）。刻意小：这是提醒，不是警报。 */
export const SOFT_CHIME_PEAK_GAIN = 0.12;

/** 渐入时长（秒）。0 会让扬声器"啪"一声。 */
export const SOFT_CHIME_ATTACK_SECONDS = 0.03;

/** 包络的极小声（不能用 0：指数渐出要求目标值严格大于 0）。 */
const FLOOR_GAIN = 0.0001;

/**
 * 把这一声排进音频上下文的时间轴。
 *
 * 纯调度：**不** `resume()`、**不** catch 异常（那是调用方
 * `estimateChimeAudio.ts` 的事）。排完就返回，声音由音频线程按时发出。
 */
export function playSoftChime(context: AudioContext): void {
  // 留 20ms 余量：紧接着 resume() 之后立刻排程是最稳的
  const startAt = context.currentTime + 0.02;

  for (const note of SOFT_CHIME_NOTES) {
    const noteStart = startAt + note.delaySeconds;
    const noteEnd = noteStart + note.durationSeconds;

    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(note.frequency, startAt);

    gain.gain.setValueAtTime(FLOOR_GAIN, noteStart);
    gain.gain.linearRampToValueAtTime(SOFT_CHIME_PEAK_GAIN, noteStart + SOFT_CHIME_ATTACK_SECONDS);
    gain.gain.exponentialRampToValueAtTime(FLOOR_GAIN, noteEnd);

    oscillator.connect(gain);
    gain.connect(context.destination);

    oscillator.start(noteStart);
    oscillator.stop(noteEnd + 0.02);
  }
}
